/**
 * Lane C0-PROPORTION (B3 port, behind figureB3.ts `FIGURE_B3`): the face at the B3 head size, drawn at the fine grid,
 * not shrunk. It is faces2/draw.ts `labFaceLayer` (the game's face: same parts, variants, expressions, age cues,
 * marks, colours) with the features placed and sized for the smaller head:
 * - eyes and brows (every variant and expression of faces2/fine.ts, both sexes, elders): each feature is carved, not
 *   resampled — whole rows and columns that repeat their neighbour are taken out until it is about three quarters of
 *   its size, so lashes, iris, pupil, shine and lid stay whole pixels (the pilot's hand redraw, done the same way for
 *   every variant); head-on the screen-right feature is carved and the left one is its mirror;
 * - nose, mouth (rest and expression mouths), blush, age cues and marks keep today's pixels (the user: the nose as the
 *   shrink has it) and only move with the head (their centre ×0.75 from the head joint);
 * - the brow is kept at least a pixel above the eye.
 * The art-resolution parts (jaw skin cells, the old style's blush, facial hair and earrings) are shrunk with the rest
 * of the head (figureB3.ts).
 * Pilot and review: docs/research/C0_PROPORTION.md.
 */
import type { View } from '../../sprites';
import { shade } from '../../palette';
import type { Look } from '../../sprites';
import { DEFAULT_FACE, type FaceLook, faceStamps } from '../face';
import { type Layer, px } from '../figure';
import { byId, type Expr, JAWS } from '../faces2/parts';
import { cueStamps, featureStamps, mouthPlaced, mouthRows, type Placed } from '../faces2/grids';
import { fineFace } from '../face2';
import { fineFeatures } from '../faces2/fine';
import { type CrispPx, crispPixels, mix, type Role, roleColours } from '../faces2/crisp';
import { exprOf, type LabFace } from '../faces2/draw';
import { faceFor } from '../faces2/pick';
import { YOUNG_STYLE } from '../faces2/live';
import { heroSkullFor } from './skullB3';
import { featureKey, proposedFeatures, type Colours, type FeatureOpts } from './faceExprB3';
import { exprFor, restOf } from './faceExprWire';

/** Head scale of B3 (figureB3.ts keeps the same number). */
export const FACE_S = 0.75;

// ── colours (faces2/draw.ts `palette`) ──

function palette(f: LabFace, skin: { skin: string; skinS: string }, hair: string): Record<string, string> {
  const lashBase = '#1c1214';
  const k = mix(lashBase, shade(hair, -0.3), 0.15);
  const browC =
    f.brow ?? (f.age === 'elder' ? mix(hair, skin.skinS, 0.25) : mix(shade(hair, -0.12), lashBase, 0.35));
  const line = mix(skin.skinS, '#5a2420', 0.45);
  return {
    k,
    l: mix(k, skin.skinS, 0.45),
    c: mix(skin.skin, skin.skinS, 0.65),
    w: '#f6f1ea',
    v: mix('#f6f1ea', skin.skinS, 0.4),
    d: shade(f.iris, -0.12),
    i: f.iris,
    h: shade(f.iris, 0.14),
    P: mix(shade(f.iris, -0.25), '#120a0a', 0.6),
    I: '#ffffff',
    b: browC,
    B: f.age === 'elder' ? mix(browC, skin.skinS, 0.3) : shade(browC, -0.06),
    s: mix(skin.skin, skin.skinS, 0.75),
    S: shade(skin.skinS, -0.06),
    H: shade(skin.skin, 0.06),
    m: line,
    n: mix(skin.skin, line, 0.5),
    L: f.lip ?? '#c4626a',
    M: '#4a1a1e',
    t: '#f4eee6',
    B2: '#e89a92',
    x: mix(skin.skin, '#f4d8cc', 0.55),
    X: mix(skin.skinS, '#9a5a50', 0.35),
    o: '#3a2420',
  };
}

// ── placing and carving ──

/** A coloured pixel at the fine grid, from the head joint's fine corner. */
type Px = { x: number; y: number; col: string };

/** Pixels moved so their box's centre is ×FACE_S from the head joint (their size kept). */
function moved(px: readonly Px[]): Px[] {
  if (!px.length) return [];
  const xs = px.map((p) => p.x);
  const ys = px.map((p) => p.y);
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  const w = Math.max(...xs) - x0 + 1;
  const h = Math.max(...ys) - y0 + 1;
  const nx = Math.round((x0 + w / 2) * FACE_S - w / 2);
  const ny = Math.round((y0 + h / 2) * FACE_S - h / 2);
  return px.map((p) => ({ ...p, x: p.x - x0 + nx, y: p.y - y0 + ny }));
}

const placedPx = (st: Placed, map: (ch: string) => string | undefined): Px[] => {
  const out: Px[] = [];
  st.rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      const col = map(row[c]!);
      if (col) out.push({ x: st.x + c, y: st.y + r, col });
    }
  });
  return out;
};

/**
 * One feature carved to about `kx`, `ky` of its size: the inner row (column) most like its neighbour goes first; a row
 * holding the feature's only pixel of some colour (a shine, a pupil) is dearer; the outer rows and columns stay.
 */
function carve(px: readonly Px[], kx: number, ky: number): Px[] {
  if (!px.length) return [];
  const x0 = Math.min(...px.map((p) => p.x));
  const y0 = Math.min(...px.map((p) => p.y));
  const W = Math.max(...px.map((p) => p.x)) - x0 + 1;
  const H = Math.max(...px.map((p) => p.y)) - y0 + 1;
  let grid: (string | null)[][] = Array.from({ length: H }, () => Array<string | null>(W).fill(null));
  for (const p of px) grid[p.y - y0]![p.x - x0] = p.col;
  const tw = W >= 5 ? Math.max(4, Math.round(W * kx)) : W;
  const th = H >= 4 ? Math.max(3, Math.round(H * ky)) : H;
  const counts = () => {
    const m = new Map<string, number>();
    for (const row of grid) for (const c of row) if (c) m.set(c, (m.get(c) ?? 0) + 1);
    return m;
  };
  const diff = (a: readonly (string | null)[], b: readonly (string | null)[]) =>
    a.reduce((n, c, i) => n + (c !== b[i] ? 1 : 0), 0);
  const dropRow = () => {
    const n = counts();
    let best = -1;
    let cost = Infinity;
    for (let r = 1; r < grid.length - 1; r++) {
      const row = grid[r]!;
      const near = Math.min(diff(row, grid[r - 1]!), diff(row, grid[r + 1]!));
      const unique = row.some((c) => c && row.filter((d) => d === c).length === n.get(c)) ? 10 : 0;
      const mid = Math.abs(r - (grid.length - 1) / 2) * 0.01;
      if (near + unique + mid < cost) [best, cost] = [r, near + unique + mid];
    }
    if (best > 0) grid.splice(best, 1);
  };
  const cols = () => grid[0]!.map((_, c) => grid.map((row) => row[c]!));
  const dropCol = () => {
    const n = counts();
    const cs = cols();
    let best = -1;
    let cost = Infinity;
    for (let c = 1; c < cs.length - 1; c++) {
      const col = cs[c]!;
      const near = Math.min(diff(col, cs[c - 1]!), diff(col, cs[c + 1]!));
      const unique = col.some((v) => v && col.filter((d) => d === v).length === n.get(v)) ? 10 : 0;
      const mid = Math.abs(c - (cs.length - 1) / 2) * 0.01;
      if (near + unique + mid < cost) [best, cost] = [c, near + unique + mid];
    }
    if (best > 0) grid = grid.map((row) => row.filter((_, i) => i !== best));
  };
  while (grid.length > th) {
    const before = grid.length;
    dropRow();
    if (grid.length === before) break;
  }
  while (grid[0]!.length > tw) {
    const before = grid[0]!.length;
    dropCol();
    if (grid[0]!.length === before) break;
  }
  const out: Px[] = [];
  grid.forEach((row, r) => row.forEach((col, c) => col && out.push({ x: x0 + c, y: y0 + r, col })));
  return out;
}

/** Runs of columns apart from each other (two eyes, two brows; one in profile), as face2.ts splits features. */
function split(px: readonly Px[]): Px[][] {
  const xs = [...new Set(px.map((p) => p.x))].sort((a, b) => a - b);
  const runs: number[][] = [];
  for (const x of xs) {
    const run = runs[runs.length - 1];
    if (run && x - run[run.length - 1]! <= 2) run.push(x);
    else runs.push([x]);
  }
  return runs.map((run) => px.filter((p) => p.x >= run[0]! && p.x <= run[run.length - 1]!));
}

/** Brow pixels tilted: the inner end `n` px lower, easing to the outer end. */
function tilted(brow: readonly Px[], innerRight: boolean, n: number): Px[] {
  if (!brow.length) return [...brow];
  const xs = brow.map((p) => p.x);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const w = Math.max(1, x1 - x0);
  return brow.map((p) => ({ ...p, y: p.y + Math.round(n * (innerRight ? (p.x - x0) / w : (x1 - p.x) / w)) }));
}

/**
 * A straight, thick brow over a head-on eye: two rows from the inner end, the outer end one row, a row of skin above
 * the lash line; `worried` lifts the inner end a pixel (hurt).
 */
function levelBrow(eye: readonly Px[], c: string, shadeC: string, worried: boolean): Px[] {
  const xs = eye.map((p) => p.x);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const top = Math.min(...eye.map((p) => p.y));
  const right = x0 + x1 >= 0;
  const out: Px[] = [];
  for (let x = x0; x <= x1; x++) {
    const fromInner = right ? x - x0 : x1 - x;
    const lift = worried && fromInner < 2 ? 1 : 0;
    out.push({ x, y: top - 3 - lift, col: c });
    if (fromInner < x1 - x0 - 1) out.push({ x, y: top - 2 - lift, col: shadeC });
  }
  return out;
}

/** An elder's head-on brow over a carved eye: level, a pixel past the eye's outer end, that end drooping a row. */
function elderBrow(eye: readonly Px[], c: string, shadeC: string): Px[] {
  const xs = eye.map((p) => p.x);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const top = Math.min(...eye.map((p) => p.y));
  const right = x0 + x1 >= 0;
  const out: Px[] = [];
  const a = right ? x0 : x0 - 1;
  const b = right ? x1 + 1 : x1;
  for (let x = a; x <= b; x++) {
    const fromOuter = right ? b - x : x - a;
    out.push({ x, y: top - 3 + (fromOuter < 2 ? 1 : 0), col: c });
  }
  // The outer end droops a pixel further (a soft tail in the brow's shade): kind, not level.
  out.push({ x: right ? b : a, y: top - 1, col: shadeC });
  return out;
}

/**
 * An elder's head-on eye opened a row (ns-art B3-R3, the warmth of the liked pre-B3 sheet): the lash of every column
 * over the eye lifted a pixel with white under it, so a pixel of white shows above the iris; the corner tips stay, so
 * the lid rounds. The brow, placed from the eye's top, rises with it.
 */
function openElderEye(eye: readonly Px[], roles: Record<Role, string>): Px[] {
  const cols = new Map<number, Px[]>();
  for (const p of eye) {
    const c = cols.get(p.x);
    if (c) c.push(p);
    else cols.set(p.x, [p]);
  }
  const out: Px[] = [];
  for (const c of cols.values()) {
    c.sort((a, b) => a.y - b.y);
    const top = c[0]!;
    if (c.length >= 2 && top.col === roles.lash) {
      out.push({ ...top, y: top.y - 1 }, { x: top.x, y: top.y, col: roles.white }, ...c.slice(1));
    } else out.push(...c);
  }
  return out;
}

/**
 * A head-on eye looking at the viewer (FACE-HERO R2 / F1): the carved eyes keep their white on the outer side and are
 * mirrored, so both irises sat toward the nose (cross-eyed). In each row the white is shared out evenly either side of
 * the iris, and one catchlight goes on the iris's upper right in both eyes (the light's side), never mirrored.
 */
function lookingAhead(eye: readonly Px[], roles: Record<Role, string>): Px[] {
  const white = new Set([roles.white, roles.whiteS]);
  const inner = new Set([roles.white, roles.whiteS, roles.iris, roles.irisL, roles.pupil, roles.shine]);
  const rows = new Map<number, Px[]>();
  for (const p of eye) {
    const r = rows.get(p.y);
    if (r) r.push({ ...p });
    else rows.set(p.y, [{ ...p }]);
  }
  const out: Px[] = [];
  for (const row of rows.values()) {
    row.sort((a, b) => a.x - b.x);
    // Runs of the eye's inside (white, iris, pupil, shine) between the lashes.
    for (let i = 0; i < row.length; ) {
      if (!inner.has(row[i]!.col)) {
        i++;
        continue;
      }
      let j = i;
      while (j + 1 < row.length && inner.has(row[j + 1]!.col) && row[j + 1]!.x === row[j]!.x + 1) j++;
      const run = row.slice(i, j + 1);
      const whites = run.filter((p) => white.has(p.col)).map((p) => p.col);
      const rest = run.filter((p) => !white.has(p.col)).map((p) => (p.col === roles.shine ? roles.irisL : p.col));
      if (rest.length) {
        const a = Math.floor(whites.length / 2);
        const cols = [...whites.slice(0, a), ...rest, ...whites.slice(a)];
        run.forEach((p, k) => (p.col = cols[k]!));
      }
      i = j + 1;
    }
    out.push(...row);
  }
  // One catchlight: the top iris row's rightmost iris pixel.
  const iris = out.filter((p) => p.col === roles.iris || p.col === roles.irisL || p.col === roles.pupil);
  if (iris.length) {
    const top = Math.min(...iris.map((p) => p.y));
    const at = iris.filter((p) => p.y === top).sort((a, b) => b.x - a.x)[0]!;
    at.col = roles.shine;
  }
  return out;
}

/** Eyes or brows of a view, carved and moved; head-on the right one mirrored to the left (centre line x −0.5). */
function features(px: readonly Px[], view: View, kx: number, ky: number): Px[][] {
  const parts = split(px);
  if (view === 'front' && parts.length === 2) {
    const right = moved(carve(parts[1]!, kx, ky));
    return [right.map((p) => ({ ...p, x: -1 - p.x })), right];
  }
  return parts.map((p) => moved(carve(p, kx, ky)));
}

/** The brow lifted until a pixel of skin parts it from the eye under it. */
function clear(brow: Px[], eyes: readonly Px[][]): Px[] {
  const bx0 = Math.min(...brow.map((p) => p.x));
  const bx1 = Math.max(...brow.map((p) => p.x));
  const under = eyes.find((e) => e.some((p) => p.x >= bx0 - 1 && p.x <= bx1 + 1));
  if (!under) return brow;
  let lift = 0;
  for (const b of brow)
    for (const e of under)
      if (Math.abs(e.x - b.x) <= 0 && e.y - b.y < 2) lift = Math.max(lift, 2 - (e.y - b.y));
  return lift ? brow.map((p) => ({ ...p, y: p.y - lift })) : brow;
}

// ── elders, drawn by hand at B3 (user 2026-10-11: the carved elder face read as a scowl with stains) ──

/** A hand stamp: rows of letters at the fine grid from the head joint's fine corner. */
type HStamp = { x: number; y: number; rows: readonly string[] };
/** Letters: eyes as crisp.ts roles (K lash/lid, I iris, P pupil, W white, w white in shade, L iris light); `R`/`r` brow
 * and its shade, `g` a wrinkle line. */
interface ElderSet {
  brows: HStamp[];
  eyes: Record<'open' | 'smile' | 'narrow' | 'wide' | 'shut' | 'squeeze', HStamp[]>;
  lines: HStamp[];
}

const mirrorStamp = (st: HStamp): HStamp => {
  const w = Math.max(...st.rows.map((r) => r.length));
  return { x: -st.x - w, y: st.y, rows: st.rows.map((r) => [...r.padEnd(w, '.')].reverse().join('')) };
};
const both = (st: HStamp): HStamp[] => [st, mirrorStamp(st)];

/** Head-on (screen-right features, mirrored), 3/4 (near eye left, far eye right) and profile (facing right). */
const ELDER: Record<'front' | 'front3' | 'side', ElderSet> = {
  front: {
    // Long brows, the inner end high, drooping to the outer end; calm eyes with a lid line.
    brows: both({ x: 1, y: 0, rows: ['RRRRR..', '.....RR'] }),
    eyes: {
      open: both({ x: 2, y: 3, rows: ['.KKKK.', 'KwIPIK', '..LL..'] }),
      smile: both({ x: 2, y: 3, rows: ['.KKKK.', 'K....K'] }),
      narrow: both({ x: 2, y: 3, rows: ['.KKKK.', 'KwIPIK'] }),
      wide: both({ x: 2, y: 2, rows: ['.KKKK.', 'KWIPIK', 'KwILIK', '..LL..'] }),
      shut: both({ x: 2, y: 4, rows: ['KKKKKK', '......'] }),
      squeeze: both({ x: 2, y: 3, rows: ['KK....', '..KKKK'] }),
    },
    // Two deliberate lines: the folds from the nose's wings toward the mouth's corners.
    lines: both({ x: 3, y: 8, rows: ['g.', 'g.', '.g'] }),
  },
  front3: {
    // Neutral brows level (QA B3-R1: a slant toward the nose read stern), only the outer tip falling.
    brows: [
      { x: -6, y: 0, rows: ['.RRRRRR', 'R......'] },
      { x: 5, y: 0, rows: ['RRRR'] },
    ],
    eyes: {
      open: [
        { x: -5, y: 3, rows: ['.KKKK.', 'KwIPIK', '.wILL.'] },
        { x: 5, y: 3, rows: ['KKKK', 'WIPK', '.LL.'] },
      ],
      smile: [
        { x: -5, y: 3, rows: ['.KKKK.', 'K....K'] },
        { x: 5, y: 3, rows: ['KKK.', '...K'] },
      ],
      narrow: [
        { x: -5, y: 3, rows: ['.KKKK.', 'KwIPIK'] },
        { x: 5, y: 3, rows: ['KKK.', 'IPIK'] },
      ],
      wide: [
        { x: -5, y: 2, rows: ['.KKKK.', 'KWIPIK', 'KwILIK', '..LL..'] },
        { x: 5, y: 2, rows: ['KKK.', 'WIPK', 'IIL.', '.L..'] },
      ],
      shut: [
        { x: -5, y: 4, rows: ['KKKKKK'] },
        { x: 5, y: 4, rows: ['KKKK'] },
      ],
      squeeze: [
        { x: -5, y: 3, rows: ['....KK', 'KKKK..'] },
        { x: 5, y: 3, rows: ['KK..', '..KK'] },
      ],
    },
    lines: [
      { x: -3, y: 8, rows: ['g.', 'g.', '.g'] },
      { x: 8, y: 8, rows: ['.g', '.g'] },
    ],
  },
  side: {
    brows: [{ x: 4, y: 0, rows: ['RRRRR'] }],
    eyes: {
      open: [{ x: 6, y: 3, rows: ['KKKK', '.IPK', '.LL.'] }],
      smile: [{ x: 6, y: 3, rows: ['KKKK', '...K'] }],
      narrow: [{ x: 6, y: 3, rows: ['KKKK', '.IPK'] }],
      wide: [{ x: 6, y: 2, rows: ['KKKK', 'WIPK', '.IL.', '.LL.'] }],
      shut: [{ x: 6, y: 4, rows: ['KKKK'] }],
      squeeze: [{ x: 6, y: 3, rows: ['KK..', '..KK'] }],
    },
    lines: [{ x: 8, y: 8, rows: ['g', 'g'] }],
  },
};

const ROLE_OF: Record<string, Role> = { K: 'lash', I: 'iris', L: 'irisL', P: 'pupil', W: 'white', w: 'whiteS' };


/**
 * A long beard and moustache at B3 for the mouth box (fine px): two moustache strands from under the nose drooping
 * past the mouth's corners, the mouth itself left clear, and the beard from under the lower lip tapering down the chin.
 */
function beardB3(m: { x0: number; x1: number; y0: number; y1: number }, view: View, hairC: string): Px[] {
  const out: Px[] = [];
  const H = hairC;
  const S = shade(hairC, -0.14);
  const put = (x: number, y: number, c = H) => out.push({ x, y, col: c });
  const { x0, x1, y0, y1 } = m;
  if (view === 'side') {
    // Profile: the moustache over the lip's front, the beard down the chin's front.
    put(x1, y0 - 1);
    put(x1 + 1, y0 - 1);
    put(x1 + 1, y0, S);
    // Tapering a pixel every other row from under the lip (ns-art B3: it read as a block stuck on the chin).
    for (let k = 0; k < 7; k++) {
      const w = Math.max(1, 4 - Math.floor(k / 2));
      const left = x1 - 2 + Math.floor(k / 3);
      for (let c = 0; c < w; c++) put(left + c, y1 + 1 + k, c === 0 && w > 1 ? S : H);
    }
    return out;
  }
  // Over the lip, a gap under the nose.
  const mid = (x0 + x1) / 2;
  for (let x = x0 - 1; x <= x1 + 1; x++) if (Math.abs(x + 0.5 - (mid + 0.5)) > 0.9) put(x, y0 - 1);
  // Strands past the corners.
  for (const [dx, side] of [
    [-1, x0],
    [1, x1],
  ] as const) {
    put(side + dx, y0, dx < 0 ? S : H);
    put(side + dx * 2, y0);
    put(side + dx * 2, y1 + 1, dx < 0 ? S : H);
  }
  // The beard under the lower lip, tapering.
  // About three quarters of today's long beard: a little wider than the mouth and its strands, tapering to the tip.
  const w0 = Math.max(x1 - x0 + 5, view === 'front' ? 10 : 8);
  for (let k = 0; k < 10; k++) {
    const w = Math.max(2, w0 - Math.floor((k * k) / 8));
    const left = Math.round(mid + 0.5 - w / 2);
    for (let c = 0; c < w; c++) put(left + c, y1 + 2 + k, c === 0 ? S : H);
  }
  return out;
}

// ── everyone else in 3/4 and profile: the pilot's hand-drawn eyes and brows (QA B3-R1: carving the traced 3/4 eyes
// left a streak under each eye and crossed lashes) ──

type Open = { eyes: HStamp[]; brows: HStamp[] };
const ADULT: Record<'male' | 'female', Record<'front3' | 'side', Open>> = {
  male: {
    front3: {
      // ns-art B3: lids lifted so the iris touches the lash line, a pixel of white on the inner side; straight,
      // thicker brows a pixel higher (the man apart from the woman).
      eyes: [
        { x: -5, y: 2, rows: ['.KKKKK', 'KIPIIW', 'KIIIIW', '.LLL..'] },
        { x: 5, y: 2, rows: ['KKKK', 'WIPK', '.IL.'] },
      ],
      brows: [
        { x: -5, y: -1, rows: ['.BBBBB', 'BBbbb.'] },
        { x: 5, y: -1, rows: ['BBBB', 'bb..'] },
      ],
    },
    side: {
      eyes: [{ x: 5, y: 2, rows: ['.KKK', 'KWPK', '.IIw', '.IL.'] }],
      brows: [{ x: 5, y: 0, rows: ['BBBB'] }],
    },
  },
  female: {
    front3: {
      eyes: [
        { x: -5, y: 3, rows: ['K.KKKK.', '.KKKKKK', '.WIPwww', '.WLIWWW', '..wLLw.'] },
        { x: 6, y: 3, rows: ['..KKK', 'KKKKK', 'WWPw.', '.LLs.'] },
      ],
      brows: [
        { x: -5, y: 0, rows: ['..BBB', 'bB...'] },
        { x: 7, y: 0, rows: ['BBB', '..b'] },
      ],
    },
    side: {
      eyes: [{ x: 5, y: 2, rows: ['.KKKK', 'KKKKK', 'WKPPw', '.KIIW', '..LL.'] }],
      brows: [{ x: 5, y: 0, rows: ['BBBB'] }],
    },
  },
};

/** An eye stamp for an expression, from its open drawing. */
function eyeFor(st: HStamp, expr: Expr): HStamp {
  const w = Math.max(...st.rows.map((r) => r.length));
  const line = (ch: string) => ch.repeat(w);
  switch (expr) {
    case 'blink':
      return { x: st.x, y: st.y + 2, rows: ['.' + 'K'.repeat(w - 2) + '.', 'K' + '.'.repeat(w - 2) + 'K'] };
    case 'smile':
      return { x: st.x, y: st.y + 1, rows: ['.' + 'K'.repeat(w - 2) + '.', 'K' + '.'.repeat(w - 2) + 'K'] };
    case 'hurt':
      return { x: st.x, y: st.y + 1, rows: ['KK' + '.'.repeat(w - 2), '.'.repeat(w - 2) + 'KK'.slice(0, 2)] };
    case 'angry':
      return { x: st.x, y: st.y + 1, rows: [line('K'), ...st.rows.slice(1, 3)] };
    default:
      return st;
  }
}

// ── the layer ──

const hairColours = (fh: string) => ({
  h: fh,
  H: shade(fh, -0.1),
  g: '#e8c27e',
  G: '#c9ced8',
  j: '#5fbf9a',
  W: '#ece6da',
  k: '#1a1010',
});

/** The game's face for a look (faces2/live.ts `gameFaceLayer`) at B3; `expr` fixed for review sheets. */
export function faceLayerB3(look: Look, expr: Expr | 'auto' = 'auto'): Layer {
  const id = look.faceId ?? `${look.skin}|${look.hair}|${look.hairStyle}|${look.eye}|${look.female ? 1 : 0}`;
  return faceB3(
    faceFor(id, look, expr, 'id', YOUNG_STYLE),
    { skin: look.skin, skinS: look.skinS },
    look.hair,
    look.beardColor,
    !!heroSkullFor(look),
    look.restFace,
  );
}

/** faces2/draw.ts `labFaceLayer` at B3 (same id and depths). */
export function faceB3(
  f: LabFace,
  skin: { skin: string; skinS: string },
  hair: string,
  facialHair?: string,
  /** The hand-drawn hero skull (skullB3.ts) carries the jaw: the art-resolution jaw cells are left out. */
  skull = false,
  /** The resting look (Look.restFace; lane FACE-EXPR). */
  restFace?: string,
): Layer {
  const pal = palette(f, skin, hair);
  const elder = f.age === 'elder';
  // A long beard (with or without the whiskers) is drawn at the fine grid at B3 (beardB3), not shrunk.
  const beardFine = f.beard === 'long';
  const roles = roleColours({ ...skin, iris: f.iris, sclera: '#f6f1ea', brow: f.brow ?? hair });
  const fh = facialHair ?? hair;
  const live: FaceLook = {
    ...DEFAULT_FACE,
    beard: beardFine ? 'none' : (f.beard ?? 'none'),
    mustache: beardFine ? 'none' : (f.mustache ?? 'none'),
    sideburns: f.sideburns ?? 'none',
    marks: f.style === 'old' && f.blush ? ['blush'] : [],
  };
  const hc = hairColours(fh);
  const jaw = byId(JAWS, f.jaw);
  const role = (p: CrispPx): Px => ({ x: p.x, y: p.y, col: roles[p.role as Role] });
  const memo = new Map<string, Px[]>();
  const exprColours: Colours = {
    K: roles.lash, W: roles.white, w: roles.whiteS, I: roles.iris, L: roles.irisL, P: roles.pupil, S: roles.shine,
    B: roles.hair, b: roles.hairS, c: roles.crease, m: pal.m!, n: pal.n!, M: pal.M!, t: pal.t!, R: pal.L!,
    r: shade(pal.L!, -0.15), h: pal.B2!, T: '#a8d0ec',
  };
  return {
    id: 'face',
    label: 'ใบหน้า (B3)',
    pieces(sk) {
      const h = sk.joints.head;
      const x = Math.round(h.x);
      const y = Math.round(h.y);
      const expr: Expr = f.expr === 'auto' ? exprOf(sk) : f.expr;
      const view = sk.view;
      const { inner: innerStamps, hair: hairStamps } = faceStamps(live, view, 'idle');
      const add = skull ? [] : (jaw.add[view] ?? []);
      const cells: [number, number][] =
        view === 'front'
          ? add.flatMap(([c, r]) => [[c, r] as [number, number], [17 - c, r] as [number, number]])
          : [...add];
      // Everything drawn at the fine grid, worked out once per view and expression.
      // Lane FACE-EXPR: the resting look and the expression the pose asks for (faceExprWire.ts).
      const fo: FeatureOpts = { rest: restOf(restFace), auto: f.expr === 'auto' ? exprFor(sk) : null };
      const memoKey = `${view}|${expr}|${sk.left ? 1 : 0}${featureKey(fo)}`;
      const known = memo.get(memoKey);
      const out: Px[] = known ?? [];
      const put = (list: readonly Placed[], map: (ch: string) => string | undefined) => {
        for (const st of list) out.push(...moved(placedPx(st, map)));
      };
      if (!known) {
        const elderView = view === 'front' || view === 'front3' || view === 'side' ? view : null;
        /** The cheek blush of the age/mark cues (dropped when the FACE-EXPR proposal draws its own). */
        const cueBlush = new Set<Px>();
        // Elders: the hand-drawn set (no carved brows, no scattered age marks); marks of anyone else carved to the
        // smaller face with the rest of it, blush moved as it is.
        const cueFace: LabFace = elder ? { ...f, age: 'adult', lines: false } : f;
        for (const st of cueStamps(cueFace, view, expr, !!sk.left)) {
          const pxs = placedPx(st, (ch) =>
          ch === 'B'
            ? mix(pal.B2!, skin.skin, 0.35)
            : ch === 'c'
              ? pal.c
              : ch === 'x'
                ? pal.x
                : ch === 'X'
                  ? pal.X
                  : ch === 'o'
                    ? pal.o
                    : undefined,
          );
          const blush = st.rows.some((r) => r.includes('B'));
          const placed = moved(blush ? pxs : carve(pxs, 0.75, 0.75));
          if (blush) for (const p of placed) cueBlush.add(p);
          out.push(...placed);
        }
        if (elder && elderView) {
          const set = ELDER[elderView];
          for (const st of set.lines) out.push(...placedPx(st as Placed, (ch) => (ch === 'g' ? pal.c : undefined)));
        }
        const startMouth = out.length;
        if (f.style === 'old') {
          const tf =
            view === 'front' || view === 'front3'
              ? fineFace(view, { eyes: 'ref', brows: 'ref', nose: f.nose, mouth: f.mouth })
              : undefined;
          if (tf?.nose) out.push(...moved(crispPixels(tf.nose, 'nose', view).map(role)));
          const covered =
            !beardFine && (f.beard === 'long' || f.mustache === 'walrus') && !['shout', 'talk', 'hurt'].includes(expr);
          if (!covered) {
            if (expr === 'neutral' || expr === 'blink') {
              if (tf?.mouth) out.push(...moved(crispPixels(tf.mouth, 'mouth', view).map(role)));
            } else {
              const mp = mouthPlaced(
                view,
                mouthRows({ id: 'old', label: '', rows: ['nmn'], lips: f.sex === 'female' }, expr, f.sex),
              );
              if (mp) put([mp], (ch) => (ch === '.' ? undefined : pal[ch]));
            }
          }
        } else {
          // A long beard is drawn below at B3 with the mouth left clear, so the mouth is placed as if uncovered.
          const ff0: LabFace = beardFine ? { ...f, beard: 'none', mustache: 'none' } : f;
          // Turned, the nose's shade one step deeper so it reads on the smaller face (QA B3-R1).
          const deep = view === 'front3' || view === 'side';
          put(featureStamps(ff0, view, expr, !!sk.left), (ch) =>
            ch === '.' ? undefined : deep && ch === 's' ? pal.S : pal[ch],
          );
        }
        if (view === 'front3' || view === 'side') {
          // Turned, nose and mouth by hand (ns-art B3): the nose a 1×2 shade tick on the shadow side and a nostril
          // dot (profile: a skin step out of the face's edge), the mouth reshaped per expression so angry, talk and
          // shout read at 3/4; placed where today's (moved) nose and mouth were.
          const was = out.splice(startMouth);
          const nose = was.filter((p) => p.y < 10);
          const mouth = was.filter((p) => p.y >= 10);
          const box = (ps: readonly { x: number; y: number }[], fx: number, fy: number) =>
            ps.length
              ? { x0: Math.min(...ps.map((p) => p.x)), x1: Math.max(...ps.map((p) => p.x)), y0: Math.min(...ps.map((p) => p.y)), y1: Math.max(...ps.map((p) => p.y)) }
              : { x0: fx, x1: fx, y0: fy, y1: fy };
          const nb = box(nose, view === 'side' ? 10 : 5, 8);
          const deep = mix(pal.S!, '#5a3a30', 0.25);
          if (view === 'front3') {
            out.push({ x: nb.x0, y: nb.y1 - 1, col: deep }, { x: nb.x0, y: nb.y1, col: deep }, { x: nb.x0 + 1, y: nb.y1 + 1, col: pal.s! });
          } else {
            out.push({ x: nb.x1 + 1, y: nb.y1 - 1, col: skin.skin }, { x: nb.x1 + 1, y: nb.y1, col: skin.skin }, { x: nb.x1, y: nb.y1 + 1, col: deep });
          }
          const mb = box(mouth, view === 'side' ? 10 : 4, 11);
          const mx = Math.round((mb.x0 + mb.x1) / 2);
          const my = mb.y0;
          const covered = beardFine ? false : (f.beard === 'long' || f.mustache === 'walrus') && !['shout', 'talk', 'hurt'].includes(expr);
          if (!covered) {
            if (expr === 'neutral' || expr === 'blink' || !mouth.length) out.push(...mouth);
            else {
              const shapes: Record<string, readonly string[]> =
                view === 'side'
                  ? { talk: ['m', 'M'], angry: ['.', 'mm'], shout: ['MM', 'MM'], smile: ['.m', 'm.'], hurt: ['m.', '.m'] }
                  : { talk: ['mMm', '.m.'], angry: ['.mmm', 'm...'], shout: ['mMMm', 'mMMm', '.mm.'], smile: ['m..m', '.mm.'], hurt: ['mmmm', 'm..m'] };
              const rows = shapes[expr] ?? ['mm'];
              const w = rows[0]!.length;
              const x0 = view === 'side' ? mb.x1 - w + 1 : mx - Math.floor(w / 2);
              const y0 = my - (rows.length > 2 ? 1 : 0);
              rows.forEach((row, r) => {
                for (let c = 0; c < row.length; c++) {
                  const ch = row[c]!;
                  if (ch !== '.') out.push({ x: x0 + c, y: y0 + r, col: ch === 'M' ? pal.M! : pal.m! });
                }
              });
            }
          }
        }
        const beardFrom = out.length;
        if (beardFine && view !== 'back' && view !== 'back3') {
          // The mouth: what was stamped below the nose.
          const mouth = out.slice(startMouth).filter((p) => p.y >= 10);
          const ms = mouth.length ? mouth : [{ x: -2, y: 11 }, { x: 1, y: 11 }];
          const box = {
            x0: Math.min(...ms.map((p) => p.x)),
            x1: Math.max(...ms.map((p) => p.x)),
            y0: Math.min(...ms.map((p) => p.y)),
            y1: Math.max(...ms.map((p) => p.y)),
          };
          out.push(...beardB3(box, view, fh));
        }
        const eyesFrom = out.length;
        if (elder && elderView) {
          const set = ELDER[elderView];
          const map = (ch: string) =>
            ch === 'R' ? pal.b : ch === 'r' ? pal.B : ROLE_OF[ch] ? roles[ROLE_OF[ch]!] : undefined;
          if (view !== 'front') for (const st of set.brows) out.push(...placedPx(st as Placed, map));
          // The eyes as everyone's (user, the liked pre-B3 sheet: friendly, readable faces): head-on the carved
          // traced eyes, turned the hand-drawn set; only the brows, folds and beard make the elder.
          if (view === 'front') {
            const ff = fineFeatures(view, f.eyes, f.brows, expr);
            const shut = expr === 'blink';
            const sp = shut ? ff?.closed : ff?.eyes;
            const eyes = sp ? features(crispPixels(sp, shut ? 'closed' : 'eyes', view).map(role), view, 0.8, 0.75) : [];
            // Long level brows over the carved eyes, the outer ends drooping a row (calm), a row of skin above the lids.
            const open = shut ? eyes : eyes.map((e) => openElderEye(e, roles));
            if (open.length === 2) for (const e of open) out.push(...elderBrow(e, pal.b!, pal.B!));
            for (const e of open) out.push(...e);
          } else {
            const amap = (ch: string) => (ch === 's' ? roles.shine : ROLE_OF[ch] ? roles[ROLE_OF[ch]!] : undefined);
            for (const st of ADULT[f.sex][elderView as 'front3' | 'side'].eyes) out.push(...placedPx(eyeFor(st, expr) as Placed, amap));
          }
        } else if (view === 'front3' || view === 'side') {
          const set = ADULT[f.sex][view];
          const map = (ch: string) =>
            ch === 'B' ? roles.hair : ch === 'b' ? roles.hairS : ch === 's' ? roles.shine : ROLE_OF[ch] ? roles[ROLE_OF[ch]!] : undefined;
          // Angry: each brow's inner end 2 px lower (a real tilt); shout: both a pixel higher.
          set.brows.forEach((st, k) => {
            const pxs = placedPx({ ...st, y: st.y + (expr === 'shout' ? -1 : 0) } as Placed, map);
            // The inner end: toward the nose — the near (first) brow's right end, the far brow's left end; profile: front.
            const innerRight = view === 'side' || k === 0;
            out.push(...(expr === 'angry' ? tilted(pxs, innerRight, 2) : pxs));
          });
          for (const st of set.eyes) out.push(...placedPx(eyeFor(st, expr) as Placed, map));
        } else if (view === 'front') {
          const ff = fineFeatures(view, f.eyes, f.brows, expr);
          const shut = expr === 'blink';
          const eyesSp = shut ? ff?.closed : ff?.eyes;
          const eyes = eyesSp
            ? features(crispPixels(eyesSp, shut ? 'closed' : 'eyes', view).map(role), view, 0.8, 0.75)
            : [];
          const brows = ff?.brows
            ? features(crispPixels(ff.brows, 'brows', view).map(role), view, 0.75, 0.6)
            : [];
          // A man's resting brows: the inner end raised a pixel, so a sharp brow keeps its line but not a frown
          // (the portrait read grumpy at 1x).
          // A man's brows head-on (ns-art B3): straight, thick and level at rest, a pixel of skin above the lash line;
          // only angry and shout keep the carved slant, so neutral and angry differ clearly.
          const level = f.sex === 'male' && expr !== 'angry' && expr !== 'shout';
          if (level && eyes.length === 2) {
            for (const e of eyes) out.push(...levelBrow(e, roles.hair, roles.hairS, expr === 'hurt'));
          } else for (const b of brows) out.push(...clear(b, eyes));
          // The hero skull's faces look at the viewer (FACE-HERO R2): irises centred, one catchlight on the lit side.
          for (const e of eyes) out.push(...(skull && !shut ? lookingAhead(e, roles) : e));
        }
        // Lane FACE-EXPR proposal (faceExprB3.ts, off in the game): today's eyes, brows and mouth swapped for it.
        const prop = proposedFeatures(f.sex, f.age, view, expr, !!sk.left, exprColours, fo);
        if (prop) {
          const keep = out
            .slice(0, eyesFrom)
            .filter((p, i) => (i < startMouth || i >= beardFrom || p.y < 10) && !(prop.blush && cueBlush.has(p)));
          out.length = 0;
          out.push(...keep, ...prop.mouth, ...prop.eyesBrows);
        }
        memo.set(memoKey, out);
      }
      return [
        {
          z: h.z + 0.01,
          draw: (g) => {
            // Jaw skin and the old style's blush at art resolution (shrunk with the head: figureB3.ts).
            for (const [c, r] of cells) px(g, x - 9 + c, y - 9 + r, 1, 1, skin.skin);
            for (const st of innerStamps)
              st.rows.forEach((row, r) => {
                for (let c = 0; c < row.length; c++)
                  if (row[c] === 'B') px(g, x + st.x + c, y + st.y + r, 1, 1, f.blushColour ?? '#eaa098');
              });
          },
          fine: (g, res) => {
            if (res !== 2) return;
            const X = x * 2;
            const Y = y * 2;
            for (const p of out) {
              g.fillStyle = p.col;
              g.fillRect(X + p.x, Y + p.y, 1, 1);
            }
          },
        },
        {
          z: h.z + 0.025,
          draw: (g) => {
            for (const st of hairStamps)
              st.rows.forEach((row, r) => {
                for (let c = 0; c < row.length; c++) {
                  const col = hc[row[c] as keyof typeof hc];
                  if (col) px(g, x + st.x + c, y + st.y + r, 1, 1, col);
                }
              });
          },
        },
      ];
    },
  };
}
